import { motion } from "framer-motion";

/**
 * Wordmark — typed ALLTASK MOVES brand mark using the same brand colors
 * and uppercase display style as the logo asset. Built as text instead of
 * an image so it renders crisply at every size, supports motion, and never
 * inherits any unwanted background from a PNG.
 *
 *   primarySize  -> tailwind size for ALLTASK
 *   secondarySize -> tailwind size for MOVES
 *   inverse       -> if true, ALLTASK becomes white (for dark backgrounds)
 *   animate       -> if true, run the letter-by-letter spring entrance
 *   stacked       -> if true (default), MOVES sits below ALLTASK with orange
 *                    rules; if false, all on one line
 */
export const Wordmark = ({
    primarySize = "text-2xl sm:text-3xl md:text-4xl",
    secondarySize = "text-xs sm:text-sm md:text-base",
    inverse = false,
    animate = false,
    stacked = true,
    className = "",
}) => {
    const letters = "ALLTASK".split("");
    const navyClass = inverse ? "text-white" : "text-[#0E2A47]";

    const Container = animate ? motion.div : "div";
    const containerProps = animate
        ? {
              initial: "hidden",
              animate: "show",
              variants: {
                  hidden: {},
                  show: { transition: { staggerChildren: 0.05, delayChildren: 0.3 } },
              },
          }
        : {};

    return (
        <Container
            {...containerProps}
            className={`inline-flex flex-col items-center leading-none ${className}`}
        >
            <p
                aria-label="ALLTASK MOVES"
                className={`font-display flex items-baseline font-extrabold tracking-tight ${navyClass} ${primarySize}`}
            >
                {letters.map((char, i) =>
                    animate ? (
                        <motion.span
                            key={`${char}-${i}`}
                            variants={{
                                hidden: { opacity: 0, y: -12, rotate: -6 },
                                show: {
                                    opacity: 1,
                                    y: 0,
                                    rotate: 0,
                                    transition: {
                                        type: "spring",
                                        stiffness: 320,
                                        damping: 22,
                                    },
                                },
                            }}
                            className="inline-block"
                        >
                            {char}
                        </motion.span>
                    ) : (
                        <span key={i}>{char}</span>
                    )
                )}
            </p>
            {stacked && (
                <div className="mt-1 flex items-center gap-1.5 sm:mt-1.5 sm:gap-2">
                    {animate ? (
                        <motion.span
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 0.6, delay: 0.85, ease: "easeOut" }}
                            className="block h-px w-5 origin-right bg-[#F26B1F] sm:w-7"
                        />
                    ) : (
                        <span className="block h-px w-5 bg-[#F26B1F] sm:w-7" />
                    )}
                    <span
                        className={`font-display font-bold tracking-[0.4em] text-[#F26B1F] ${secondarySize}`}
                        style={{ paddingLeft: "0.4em" }}
                    >
                        MOVES
                    </span>
                    {animate ? (
                        <motion.span
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 0.6, delay: 0.85, ease: "easeOut" }}
                            className="block h-px w-5 origin-left bg-[#F26B1F] sm:w-7"
                        />
                    ) : (
                        <span className="block h-px w-5 bg-[#F26B1F] sm:w-7" />
                    )}
                </div>
            )}
        </Container>
    );
};

export default Wordmark;
